import React from 'react';
import { useBemCN } from '@gik/core/utils/bemBlock';
import { finishRegistrationSearchParam, signupEmailSearchParam } from '@gik/finish-registration/consts';
import {
  acceptInvitation,
  useInvitationEmailAddressFromToken,
} from '@gik/finish-registration/api/FinishRegistrationAPI';
import { Avatar, Button, Input, PageSection, SvgIcon, UI } from '@gik/ui';
import { shouldShowPagePicture } from '@gik/finish-registration/utils';
import { useSearchParam } from 'react-use';
import { useInkind } from '@gik/api/inkinds/inkind';
import { SignupForm } from '@gik/auth';
import UserRegistrationData from '@gik/core/models/gik/auth/UserRegistrationData';
import { userSignUp } from '@gik/api/users/auth';
import { performLogin } from '@gik/auth/utils/auth';
import ExternalLoginForm from '@gik/auth/components/ExternalLogin/ExternalLoginForm';
import { signupFormId } from '@gik/auth/components/Signup/SignupForm';

export interface IFinishRegistrationProps {
  routeId: string;
  buttonsPortal?: React.MutableRefObject<HTMLDivElement>;
}

// todo i18n
export default function FinishRegistration({ routeId, buttonsPortal }: IFinishRegistrationProps) {
  const bem = useBemCN('finish-registration');
  const invitationToken = useSearchParam(finishRegistrationSearchParam);
  const signUpEmailAddress = useSearchParam(signupEmailSearchParam);
  const {
    data: { email: invitationEmailAddress } = { invitationEmailAddress: undefined },
  } = useInvitationEmailAddressFromToken(invitationToken);
  const { data: inkindPage } = useInkind(routeId);

  async function onFinishRegistrationSubmit(formValues) {
    const userData: UserRegistrationData = {
      firstName: formValues.firstName,
      lastName: formValues.lastName,
      password: formValues.password,
      emailAddress: formValues.email,
    };

    if (signUpEmailAddress) {
      userData.inkindRouteId = inkindPage.routeId;
    }

    const { id: userId } = await userSignUp(userData);

    const url = new URL(window.location.toString());
    url.searchParams.delete(signupEmailSearchParam);
    window.history.pushState({}, '', url.toString());
    performLogin(userId, false);
    UI.closeDialog();
  }

  function buttons(valid: boolean, submitting: boolean) {
    return (
      <div {...bem('finish-signup-button')}>
        <Button
          variant={'primary'}
          type="submit"
          form={signupFormId}
          disabled={submitting || !valid}
          loading={submitting}
        >
          Finish sign-up
        </Button>
      </div>
    );
  }

  return (
    <div {...bem('wrapper')}>
      <section {...bem('page-info')}>
        <PageSection variant={'aqua'} {...bem('instructions')}>
          <div {...bem('instructions-wrapper')}>
            {shouldShowPagePicture(inkindPage?.thumb_medium) && (
              <div>
                <Avatar
                  variant={'primary'}
                  circle={false}
                  showBorder
                  size={'2xl'}
                  imageUrl={inkindPage?.thumb_medium}
                />
              </div>
            )}
            <div {...bem('instructions-copy')}>
              <div {...bem('title')}>Welcome to Give InKind!</div>
              <div {...bem('subtitle')}>
                Finish the quick registration below so you can start helping{' '}
                <span {...bem('page-title')}>{inkindPage?.title}</span>
              </div>
            </div>
          </div>
        </PageSection>
      </section>

      <ExternalLoginForm />

      <p {...bem('or-via-email')}>…or sign-up using email:</p>

      <div {...bem('form-wrapper')}>
        <section {...bem('email-address-section')}>
          <Input
            disabled
            value={invitationEmailAddress}
            append={
              <Button
                circle
                variant={'primary-plain'}
                tooltip={
                  'To use a different email, please ask the Organizer to resend the invitation to that email address.'
                }
              >
                <SvgIcon name={'InformationCircleOutline'} />
              </Button>
            }
          />
        </section>

        <SignupForm
          {...bem('sign-up-form')}
          hideEmailField
          initialValues={{
            email: invitationEmailAddress,
          }}
          buttonsOverride={buttons}
          buttonsPortal={() => buttonsPortal?.current}
          onSubmitOverride={onFinishRegistrationSubmit}
        />
      </div>
    </div>
  );
}
